<template>
  <form class="division-form" @submit.prevent="save">
    <div v-if="loading" class="division-form__state">
      Загрузка данных площадки...
    </div>

    <div v-else-if="loadError" class="division-form__state division-form__state_error">
      <span>{{ loadError }}</span>
      <CButton color="secondary" type="button" @click="hide">Закрыть</CButton>
    </div>

    <template v-else>
      <section class="division-form__section">
        <div class="division-form__section-heading">
          <span class="division-form__section-icon mdi mdi-office-building-outline"></span>
          <div>
            <h3>Основное</h3>
            <p>Название и подключение площадки</p>
          </div>
        </div>

        <div class="division-form__grid">
          <label class="division-form__field division-form__field_wide">
            <span>Название <b>*</b></span>
            <CFormInput
              v-model="form.name"
              autocomplete="off"
              :invalid="submitted && !form.name.trim()"
              placeholder="Например, Офис на Ленина"
            />
            <small v-if="submitted && !form.name.trim()" class="division-form__error">
              Название не может быть пустым
            </small>
          </label>

          <label class="division-form__field division-form__field_wide">
            <span>Адрес</span>
            <CFormInput v-model.trim="form.address" autocomplete="off" placeholder="Город, улица, дом" />
          </label>

          <label class="division-form__field">
            <span>URL</span>
            <CFormInput v-model.trim="form.url" autocomplete="off" placeholder="https://..." />
          </label>

          <label class="division-form__field">
            <span>Версия</span>
            <CFormInput v-model.trim="form.version" autocomplete="off" placeholder="Например, 2.1" />
          </label>

          <label class="division-form__field division-form__field_wide">
            <span>ID группы</span>
            <CFormInput
              v-model.trim="form.divisionGroupId"
              autocomplete="off"
              placeholder="UUID группы"
            />
          </label>
        </div>
      </section>

      <div class="division-form__reporting">
        <div>
          <strong>Отчётность</strong>
          <p>Разрешить площадке отправлять данные для отчётов</p>
        </div>
        <button
          id="division-enable-reporting"
          class="division-form__switch"
          :class="{ 'division-form__switch_active': form.enableReporting }"
          type="button"
          role="switch"
          :aria-checked="form.enableReporting"
          aria-label="Включить отчётность"
          @click="form.enableReporting = !form.enableReporting"
        >
          <span></span>
        </button>
      </div>

      <details class="division-form__advanced">
        <summary>
          <span class="mdi mdi-tune-variant"></span>
          <span>
            <strong>Связанные устройства</strong>
            <small>Камеры, домофоны и контроллеры доступа</small>
          </span>
          <span class="mdi mdi-chevron-down division-form__chevron"></span>
        </summary>

        <div class="division-form__advanced-fields">
          <label class="division-form__field">
            <span>Камеры</span>
            <textarea
              v-model="lists.camList"
              class="form-control division-form__textarea"
              placeholder="По одному значению в строке"
            ></textarea>
          </label>

          <label class="division-form__field">
            <span>Домофоны</span>
            <textarea
              v-model="lists.intercomList"
              class="form-control division-form__textarea"
              placeholder="По одному значению в строке"
            ></textarea>
          </label>

          <label class="division-form__field">
            <span>Контроллеры доступа</span>
            <textarea
              v-model="lists.accessControllerList"
              class="form-control division-form__textarea"
              placeholder="По одному значению в строке"
            ></textarea>
          </label>
        </div>
      </details>

      <div class="division-form__actions">
        <button class="division-form__cancel" type="button" :disabled="saving" @click="hide">
          Отмена
        </button>
        <CButton color="primary" type="submit" :disabled="saving">
          {{ saving ? 'Сохранение...' : 'Сохранить' }}
        </CButton>
      </div>
    </template>
  </form>
</template>

<script>
import { CButton, CFormInput } from '@coreui/bootstrap-vue'
import { ApiSys } from '@/services/api'

const emptyDivision = () => ({
  guid: '',
  divisionGroupId: '',
  name: '',
  address: '',
  camList: [],
  version: '',
  url: '',
  intercomList: [],
  accessControllerList: [],
  enableReporting: true
})

export default {
  name: 'DivisionRecordEdit',
  components: {
    CButton,
    CFormInput
  },
  props: {
    division: {
      type: Object,
      default: null
    },
    hide: {
      type: Function,
      required: true
    }
  },
  emits: ['saved'],
  data() {
    return {
      loading: false,
      loadError: '',
      saving: false,
      submitted: false,
      form: emptyDivision(),
      lists: {
        camList: '',
        intercomList: '',
        accessControllerList: ''
      }
    }
  },
  async created() {
    if (!this.division?.guid) {
      return
    }

    this.loading = true
    const response = await ApiSys.getAdminDivisionRecord(this.division.guid)
    this.loading = false

    if (response?.error) {
      this.loadError = this.errorMessage(response, 'Не удалось загрузить площадку')
      this.$toast.error(this.loadError)
      return
    }

    this.applyDivision(response?.data || response)
  },
  methods: {
    applyDivision(division) {
      const source = { ...emptyDivision(), ...division }
      source.enableReporting = source.enableReporting !== false
      this.form = source
      this.lists = {
        camList: this.formatList(source.camList),
        intercomList: this.formatList(source.intercomList),
        accessControllerList: this.formatList(source.accessControllerList)
      }
    },
    formatList(value) {
      return Array.isArray(value) ? value.join('\n') : ''
    },
    parseList(value) {
      return String(value || '')
        .split(/[\n,]/)
        .map(item => item.trim())
        .filter(Boolean)
    },
    errorMessage(response, fallback) {
      return response?.error?.response?.data?.message ||
        response?.error?.response?.data?.error?.message ||
        response?.error?.message ||
        fallback
    },
    async save() {
      this.submitted = true
      const name = this.form.name.trim()
      if (!name) {
        this.$toast.error('Название площадки не может быть пустым')
        return
      }

      const payload = {
        ...this.form,
        name,
        camList: this.parseList(this.lists.camList),
        intercomList: this.parseList(this.lists.intercomList),
        accessControllerList: this.parseList(this.lists.accessControllerList),
        enableReporting: Boolean(this.form.enableReporting)
      }
      if (!this.division?.guid) {
        delete payload.guid
      }

      this.saving = true
      const response = this.division?.guid
        ? await ApiSys.updateAdminDivisionRecord(this.division.guid, payload)
        : await ApiSys.createAdminDivisionRecord(payload)
      this.saving = false

      if (response?.error) {
        this.$toast.error(this.errorMessage(response, 'Не удалось сохранить площадку'))
        return
      }

      this.$toast.success(this.division?.guid ? 'Площадка обновлена' : 'Площадка создана')
      this.$emit('saved')
      this.hide()
    }
  }
}
</script>

<style scoped>
.division-form {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.division-form__state {
  padding: 2rem;
  color: #aeb7c2;
  text-align: center;
}

.division-form__state_error {
  color: #ff8b8b;
}

.division-form__section {
  padding: 1rem;
  background: rgba(255, 255, 255, 0.025);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 12px;
}

.division-form__section-heading {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 1rem;
}

.division-form__section-heading h3,
.division-form__section-heading p,
.division-form__reporting p {
  margin: 0;
}

.division-form__section-heading h3 {
  color: #edf1f5;
  font-size: 0.9rem;
  font-weight: 650;
}

.division-form__section-heading p,
.division-form__reporting p {
  margin-top: 0.1rem;
  color: #697582;
  font-size: 0.7rem;
}

.division-form__section-icon {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  color: #8ec5fa;
  background: rgba(56, 154, 240, 0.12);
  border-radius: 9px;
  font-size: 1.1rem;
}

.division-form__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
}

.division-form__field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
}

.division-form__field > span {
  color: #aeb8c2;
  font-size: 0.74rem;
  font-weight: 550;
}

.division-form__field > span b {
  color: #ff8b8b;
}

.division-form__field_wide {
  grid-column: 1 / -1;
}

.division-form__textarea {
  min-height: 90px;
  resize: vertical;
}

.division-form__error {
  color: #ff7b7b;
}

.division-form__reporting {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.8rem 1rem;
  background: rgba(56, 154, 240, 0.055);
  border: 1px solid rgba(56, 154, 240, 0.12);
  border-radius: 11px;
}

.division-form__reporting strong {
  color: #dce4ec;
  font-size: 0.8rem;
  font-weight: 600;
}

.division-form__switch {
  position: relative;
  flex: 0 0 42px;
  width: 42px;
  height: 24px;
  padding: 0;
  background: #4d5863;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
  transition: background 0.18s ease, box-shadow 0.18s ease;
}

.division-form__switch span {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
  transition: transform 0.18s ease;
}

.division-form__switch_active {
  background: #389af0;
  box-shadow: 0 0 0 3px rgba(56, 154, 240, 0.12);
}

.division-form__switch_active span {
  transform: translateX(18px);
}

.division-form__switch:focus-visible {
  outline: 2px solid #8ec5fa;
  outline-offset: 2px;
}

.division-form__advanced {
  background: rgba(255, 255, 255, 0.018);
  border: 1px solid rgba(255, 255, 255, 0.065);
  border-radius: 11px;
}

.division-form__advanced summary {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.8rem 1rem;
  color: #8b97a3;
  cursor: pointer;
  list-style: none;
  user-select: none;
}

.division-form__advanced summary::-webkit-details-marker {
  display: none;
}

.division-form__advanced summary > .mdi:first-child {
  font-size: 1rem;
}

.division-form__advanced summary > span:nth-child(2) {
  display: flex;
  flex: 1;
  flex-direction: column;
}

.division-form__advanced summary strong {
  color: #c1cad3;
  font-size: 0.78rem;
  font-weight: 600;
}

.division-form__advanced summary small {
  margin-top: 0.1rem;
  color: #626e7a;
  font-size: 0.66rem;
}

.division-form__chevron {
  transition: transform 0.18s ease;
}

.division-form__advanced[open] .division-form__chevron {
  transform: rotate(180deg);
}

.division-form__advanced-fields {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  padding: 0 1rem 1rem;
}

.division-form__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-top: 0.25rem;
}

.division-form__cancel {
  padding: 0.45rem 0.8rem;
  color: #8995a1;
  background: transparent;
  border: 0;
  border-radius: 6px;
  cursor: pointer;
}

.division-form__cancel:hover:not(:disabled) {
  color: #d1d8df;
  background: rgba(255, 255, 255, 0.05);
}

@media (max-width: 767px) {
  .division-form__grid,
  .division-form__advanced-fields {
    grid-template-columns: 1fr;
  }

  .division-form__field_wide {
    grid-column: auto;
  }
}
</style>
